import { useRef, useState } from 'react';
import {
  MIN_COMMENT_WORDS,
  OPEN_COMMENT_MAX_LENGTH,
  commentIsAttributed,
  countWords,
  isCommentSubstantial,
} from '@genoma/shared';

import { Brand } from '../../components/Brand.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { useRiseIn } from '../../lib/use-gsap.ts';
import { useSurveyStore } from '../../stores/survey.store.ts';

/**
 * The one question answered in words.
 *
 * It comes last, after every behavior, because a rater can only write about a
 * person once they have thought about all twelve — and because arriving here
 * means the scored part is already safe.
 *
 * Required. It was optional at first, on the grounds that forcing prose
 * produces filler — but optional meant a rater could sit on this screen with
 * every score in and nothing submitted, while the dashboard, counting answers,
 * showed them as finished. Whoever was chasing participation could not tell
 * them apart from somebody who had actually finished, so the answer to "who
 * still needs a nudge" was wrong.
 *
 * What is missing is said before the button is reached for, and the button
 * names the count rather than just refusing.
 *
 * Whether the words will carry a name is stated before the box, not after it,
 * and per person rather than per survey — a rater is routinely supervisor to
 * some of the people on this screen and stakeholder to others, and only the
 * supervisor lines are attributed. Someone who would phrase a criticism
 * differently knowing their name travels with it deserves to know which one
 * they are doing, on each box.
 */
export const CommentsScreen = () => {
  const { t } = useI18n();
  const store = useSurveyStore();
  const screenRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);

  const { survey } = store;
  useRiseIn(screenRef, '[data-rise]', []);

  if (!survey) return null;

  const isSelf = survey.kind === 'SELF';
  const written = survey.evaluated.filter((person) =>
    isCommentSubstantial(store.comments[person.id] ?? ''),
  ).length;
  const missing = survey.evaluated.length - written;

  const finish = async () => {
    setSubmitting(true);
    await store.submit();
    setSubmitting(false);
  };

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-5">
      <Brand organization={survey.brand.organization} program={survey.brand.program} />

      <main ref={screenRef} className="flex-1 pt-4 pb-6">
        <div data-rise>
          <span className="inline-flex rounded-[20px] border border-tint-line bg-tint px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.14em] text-teal-deep uppercase">
            {t('comments.kicker')}
          </span>

          <h1 className="mt-3.5 text-2xl leading-[1.14] font-extrabold tracking-[-0.02em]">
            {isSelf ? t('comments.titleSelf') : t('comments.title')}
          </h1>
          <p className="mt-2 max-w-[62ch] text-sm text-soft">
            {isSelf ? t('comments.leadSelf') : t('comments.lead')}
          </p>

          {isSelf && (
            <p className="mt-3 border-l-2 border-line pl-2.5 text-[13px] text-faint">
              {t('comments.privacySelf')}
            </p>
          )}
        </div>

        {survey.evaluated.map((person) => {
          const value = store.comments[person.id] ?? '';
          const firstName = person.fullName.split(' ')[0] ?? person.fullName;
          const attributed = !isSelf && commentIsAttributed(person.relationship);
          const words = countWords(value);
          const short = words > 0 && words < MIN_COMMENT_WORDS;

          return (
            <section key={person.id} data-rise className="mt-6">
              <label className="block" htmlFor={`comment-${person.id}`}>
                <span className="block text-[15px] leading-snug font-bold">
                  {isSelf
                    ? t('comments.promptSelf')
                    : t('comments.prompt', { name: person.fullName })}
                </span>
                <span className="mt-1 block text-[13px] text-faint">
                  {t('comments.promptHelp')}
                </span>
              </label>

              {/* Said before the box, never after, and per person: this rater
                  may be supervisor to the person above and stakeholder to the
                  one below. */}
              {/* A rule and small type, not a bordered box: sitting directly
                  above the textarea, anything card-shaped reads as a filled
                  field. */}
              {!isSelf && (
                <p
                  className={[
                    'mt-2 border-l-2 pl-2.5 text-[12.5px] leading-snug',
                    attributed ? 'border-teal text-teal-deep' : 'border-line text-faint',
                  ].join(' ')}
                >
                  {attributed ? t('comments.privacyAttributed') : t('comments.privacyPooled')}
                </p>
              )}

              <textarea
                id={`comment-${person.id}`}
                value={value}
                rows={4}
                maxLength={OPEN_COMMENT_MAX_LENGTH}
                onChange={(event) => store.writeComment(person.id, event.target.value)}
                onBlur={() => void store.flush()}
                placeholder={
                  isSelf
                    ? t('comments.placeholderSelf')
                    : t('comments.placeholder', { name: firstName })
                }
                className={[
                  'mt-2.5 w-full resize-y rounded-xl border bg-paper px-3.5 py-3 text-[15px] leading-relaxed outline-none placeholder:text-faint focus:border-teal',
                  // Amber, not red: nothing is wrong yet, it is just not done.
                  isCommentSubstantial(value) ? 'border-line' : 'border-[#E3C88A]',
                ].join(' ')}
              />

              {/* Said only once somebody has started and stopped too soon.
                  Shown from the first keystroke it would read as a target, and
                  this question has no right length — only a floor. */}
              {short && (
                <p className="mt-1 text-[12px] text-[#8A6A1F]">
                  {t('comments.tooShort', { count: MIN_COMMENT_WORDS - words })}
                </p>
              )}

              {/* The ceiling, only near it. */}
              {value.length > OPEN_COMMENT_MAX_LENGTH * 0.9 && (
                <p className="mt-1 text-right font-mono text-[11px] text-faint">
                  {value.length} / {OPEN_COMMENT_MAX_LENGTH}
                </p>
              )}
            </section>
          );
        })}

        {store.saveState === 'error' && (
          <p className="mt-4 text-center text-[13px] text-red-700">
            {store.errorMessage ?? t('errors.offline')}
          </p>
        )}

        <p className="mt-7 border-t border-line-soft pt-4 text-[13px] text-faint">
          {survey.evaluated.length > 1
            ? t('comments.progress', { written, total: survey.evaluated.length })
            : t('comments.requiredOne')}
        </p>
      </main>

      <div className="sticky bottom-0 mt-3.5 flex items-center gap-3 bg-gradient-to-b from-sand/0 to-sand py-3.5 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={() => void store.goBack()}
          disabled={submitting}
          className="cursor-pointer border-none bg-transparent px-1.5 py-3 font-mono text-xs tracking-[0.06em] text-soft uppercase disabled:opacity-30"
        >
          ← {t('common.back')}
        </button>

        <button
          type="button"
          onClick={() => void finish()}
          disabled={submitting || missing > 0}
          className="ml-auto max-w-[340px] flex-1 cursor-pointer rounded-[13px] border-none bg-teal px-4 py-3.5 text-[15px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:cursor-default disabled:bg-[#CDCAC2] disabled:shadow-none"
        >
          {submitting
            ? t('common.saving')
            : missing > 0
              ? t('comments.stillMissing', { count: missing })
              : t('common.finish')}
        </button>
      </div>
    </div>
  );
};
